import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Botmaa Component
 * Reusable component for displaying BotMaa logo and branding
 * Supports dynamic sizing, spacing, and responsive design
 */
@Component({
  selector: 'app-botmaa',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './botmaa.html',
  styleUrl: './botmaa.scss',
})
export class Botmaa {
  // Logo image source
  @Input() logoSrc: string = 'assets/Logo.png';
  @Input() logoAlt: string = 'Logo';

  // Logo sizing - responsive
  @Input() logoWidth: string = 'w-10'; // Default: w-10 (40px)
  @Input() logoHeight: string = 'h-14'; // Default: h-14 (56px)
  @Input() logoMarginRight: string = 'mr-3'; // Default: mr-3

  // Title text
  @Input() titleText: string = 'BotMaa';
  @Input() titleSize: string = 'text-4xl'; // Default: text-4xl
  @Input() titleColor: string = 'text-botmaaOrange'; // Color for "Maa" part

  // Box styling
  @Input() showBox: boolean = true; // Show border box around logo
  @Input() boxPadding: string = 'p-2'; // Padding inside box
  @Input() boxBorder: string = 'border-2 border-gray-300'; // Border styling

  // Footer text
  @Input() showFooterText: boolean = true; // Show "Powered by" text
  @Input() footerText: string = 'Powered by Fibonautti Technologies Pvt Ltd';
  @Input() footerTextSize: string = 'text-xs'; // Footer text size
  @Input() footerTextColor: string = 'text-gray-500'; // Footer text color
  @Input() footerMarginTop: string = 'mt-2'; // Margin top for footer

  // Container spacing
  @Input() containerMarginBottom: string = 'mb-16'; // Bottom margin for container

  // Responsive classes for logo (for mobile/tablet)
  @Input() responsiveLogoWidth: string = ''; // e.g., 'sm:w-10' for responsive
  @Input() responsiveLogoHeight: string = ''; // e.g., 'sm:h-14' for responsive
  @Input() responsiveLogoMargin: string = ''; // e.g., 'sm:mr-3' for responsive
  @Input() responsiveTitleSize: string = ''; // e.g., 'sm:text-4xl' for responsive
  @Input() responsiveBoxPadding: string = ''; // e.g., 'sm:p-3' for responsive
  @Input() responsiveFooterSize: string = ''; // e.g., 'sm:text-xs' for responsive
  @Input() responsiveFooterMargin: string = ''; // e.g., 'sm:mt-2' for responsive
  @Input() responsiveContainerMargin: string = 'mb-0'; // e.g., 'sm:mb-8' for responsive

  /**
   * Get combined logo width classes
   */
  getLogoWidthClasses(): string {
    return [this.logoWidth, this.responsiveLogoWidth].filter(Boolean).join(' ');
  }

  /**
   * Get combined logo height classes
   */
  getLogoHeightClasses(): string {
    return [this.logoHeight, this.responsiveLogoHeight].filter(Boolean).join(' ');
  }

  /**
   * Get combined logo margin classes
   */
  getLogoMarginClasses(): string {
    return [this.logoMarginRight, this.responsiveLogoMargin].filter(Boolean).join(' ');
  }

  /**
   * Get combined title size classes
   */
  getTitleSizeClasses(): string {
    return [this.titleSize, this.responsiveTitleSize].filter(Boolean).join(' ');
  }

  /**
   * Get combined box padding classes
   */
  getBoxPaddingClasses(): string {
    return [this.boxPadding, this.responsiveBoxPadding].filter(Boolean).join(' ');
  }

  /**
   * Get combined footer text size classes
   */
  getFooterSizeClasses(): string {
    return [this.footerTextSize, this.responsiveFooterSize].filter(Boolean).join(' ');
  }

  /**
   * Get combined footer margin classes
   */
  getFooterMarginClasses(): string {
    return [this.footerMarginTop, this.responsiveFooterMargin].filter(Boolean).join(' ');
  }

  /**
   * Get combined container margin classes
   */
  getContainerMarginClasses(): string {
    return [this.containerMarginBottom, this.responsiveContainerMargin].filter(Boolean).join(' ');
  }

  /**
   * Split title text into "Bot" and "Maa" parts
   */
  getTitleParts(): { first: string; second: string } {
    const parts = this.titleText.split('Maa');
    return {
      first: parts[0] || 'Bot',
      second: 'Maa' + (parts[1] || ''),
    };
  }
}
